export function _hasOwnProperty( object: any, property: string ) {
  return Object.prototype.hasOwnProperty.call( object, property );
}

export function deepEqualByValue( value1: any, value2: any ): boolean {
  if ( value1 === value2 ) {
    return true;
  }

  if ( Array.isArray( value1 ) && Array.isArray( value2 ) ) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return arraysAreEqual( value1, value2 );
  }

  if ( typeof value1 === 'object' && typeof value2 === 'object' ) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return deepObjectsAreEqualByValue( value1, value2 );
  }

  return false;
}

export function arraysAreEqual( array1: any[], array2: any[] ): boolean {
  if ( array1.length !== array2.length ) {
    return false;
  }

  for ( let i = 0; i < array1.length; i++ ) {
    if ( !deepEqualByValue( array1[i], array2[i] ) ) {
      return false;
    }
  }

  return true;
}

/**
 * Shallowly compares two objects by value. They are considered equal if their top-level properties
 * are indentical. For properties that are arrays and objects, they must refer to the same entity in
 * both to be considered equal. Use `deepObjectsAreEqualByValue` to recurse into arrays and objects
 * to compare them by value.
 *
 * This is typed such that given the two input objects, one should extend another (in either order).
 */
export function objectsAreEqualByValue<T extends Record<any, any>, U extends Record<any, any>>(
  object1: T extends U ? T : U,
  object2: T extends U ? U : T,
): boolean {
  if ( ( object1 as unknown ) === ( object2 as unknown ) ) {
    return true;
  }

  const keys1 = Object.keys( object1 );
  const keys2 = Object.keys( object2 );

  if ( keys1.length !== keys2.length ) {
    return false;
  }

  for ( let key of keys1 ) {
    if ( object1[key] !== object2[key] ) {
      return false;
    }
  }

  return true;
}

/**
 * Deeply compare two objects by value. Properties that are arrays or objects are recursed through
 * `deepEqualByValue`. Equality is ultimately determined through only scalar comparisons.
 *
 * This is typed such that given the two input objects, one should extend another (in either order).
 */
export function deepObjectsAreEqualByValue( object1: Object, object2: Object ): boolean {
  if ( object1 === object2 ) {
    return true;
  }

  const keys1 = Object.keys( object1 );
  const keys2 = Object.keys( object2 );

  if ( keys1.length !== keys2.length ) {
    return false;
  }

  for ( let key of keys1 ) {
    if ( !deepEqualByValue( ( object1 as any )[key], ( object2 as any )[key] ) ) {
      return false;
    }
  }

  return true;
}
